import { useState } from "react";
import axios from "axios";
import { AFF_API, getAffHeaders } from "../../context/AffAuthContext";
import { IdentificationCard, CheckCircle, X, ShieldCheck } from "@phosphor-icons/react";
import { Input } from "../ui/input";
import { toast } from "sonner";

/**
 * One-time PAN capture + verification via Cashfree PAN API.
 * On success, backend stores the PAN on kyc_personal.pan and future
 * withdrawals reuse it (read-only). Name on PAN must fuzzy-match the
 * user's Aadhaar/KYC name — mismatch is a hard block.
 */
export default function PanVerifyModal({ open, onClose, onVerified, initialPan }) {
  const [pan, setPan] = useState((initialPan || "").toUpperCase());
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const clean = pan.trim().toUpperCase();
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(clean)) {
      setError("Enter a valid PAN in format AAAAA9999A");
      return;
    }
    setVerifying(true);
    try {
      const res = await axios.post(
        `${AFF_API}/affiliate/kyc/pan/verify`,
        { pan: clean },
        { headers: getAffHeaders() },
      );
      toast.success(`PAN verified · ${res.data.name_on_pan || "OK"}`);
      onVerified?.(res.data);
      onClose?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = typeof d === "string" ? d : "PAN verification failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4" data-testid="pan-verify-modal">
      <form onSubmit={submit} className="w-full max-w-md rounded-xl border border-zinc-800/60 bg-zinc-900 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <IdentificationCard size={20} weight="duotone" className="text-sky-400" />
            <h3 className="text-sm font-bold text-zinc-50" style={{ fontFamily: "Manrope" }}>
              Verify your PAN
            </h3>
          </div>
          <button type="button" onClick={onClose} data-testid="close-pan-modal" className="text-zinc-500 hover:text-zinc-200">
            <X size={16} />
          </button>
        </div>

        <div className="mb-4 p-3 rounded-md border border-sky-500/30 bg-sky-500/5 text-[11px] text-sky-200 leading-relaxed">
          <div className="flex items-start gap-2">
            <ShieldCheck size={14} weight="duotone" className="text-sky-400 mt-0.5 flex-shrink-0" />
            <div>
              PAN is verified once against the official database.
              Once verified, you will be able to withdraw any amount. Without a verified PAN card, you can only withdraw within ₹20,000 every month.
              The name on PAN must match your Aadhaar KYC name.
            </div>
          </div>
        </div>

        <div className="mb-4">
          <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">
            Permanent Account Number (PAN)
          </label>
          <Input
            data-testid="pan-input"
            value={pan}
            onChange={(e) => setPan(e.target.value.toUpperCase().slice(0, 10))}
            placeholder="AAAAA9999A"
            maxLength={10}
            className="bg-zinc-950 border-zinc-800 text-zinc-100 font-mono tracking-widest text-lg"
          />
          <p className="text-[10px] text-zinc-500 mt-1">10 characters · 5 letters + 4 digits + 1 letter</p>
        </div>

        {error && (
          <div className="mb-4 p-2.5 rounded-md bg-rose-500/10 border border-rose-500/30 text-[11px] text-rose-300" data-testid="pan-error">
            {error}
          </div>
        )}

        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2.5 rounded-lg text-xs border border-zinc-800 text-zinc-400 hover:text-zinc-100">
            Cancel
          </button>
          <button
            type="submit"
            disabled={verifying || pan.length !== 10}
            data-testid="verify-pan-btn"
            className="flex-1 py-2.5 rounded-lg text-xs font-bold bg-sky-600 text-white hover:bg-sky-500 disabled:opacity-50 flex items-center justify-center gap-1.5"
          >
            {verifying ? (
              "Verifying with Income Tax…"
            ) : (
              <>
                <CheckCircle size={14} weight="fill" />
                Verify PAN
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
